import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { TrendingUp, TrendingDown, ArrowUpRight } from 'lucide-react';
import { formatNumber } from '../../utils/helpers';

type AccentColor = 'blue' | 'green' | 'red' | 'amber' | 'purple';

interface StatCardProps {
  title: string;
  value: number;
  icon: ReactNode;
  trend?: number;
  trendLabel?: string;
  accent?: AccentColor;
  /** When set, the whole card is a link to this route. */
  to?: string;
}

const accentConfig: Record<AccentColor, { icon: string; glow: string; gradient: string }> = {
  blue:   { icon: 'bg-[#3b82f6]/15 text-[#3b82f6]', glow: '', gradient: 'gradient-blue' },
  green:  { icon: 'bg-[#22c55e]/15 text-[#22c55e]', glow: '', gradient: 'gradient-green' },
  red:    { icon: 'bg-[#ef4444]/15 text-[#ef4444]', glow: '', gradient: 'gradient-red' },
  amber:  { icon: 'bg-[#f59e0b]/15 text-[#f59e0b]', glow: '', gradient: 'gradient-amber' },
  purple: { icon: 'bg-[#a855f7]/15 text-[#a855f7]', glow: '', gradient: 'gradient-purple' },
};

export function StatCard({ title, value, icon, trend, trendLabel, accent = 'blue', to }: StatCardProps) {
  const isPositive = trend !== undefined && trend > 0;
  const isNegative = trend !== undefined && trend < 0;
  const cfg = accentConfig[accent];

  const card = (
    <div
      className={`stat-card-accent accent-${accent} bg-[#141420] border border-[#252535] rounded-2xl p-5 card-hover h-full ${
        to ? 'group-hover:border-[#3a3a52] transition-colors' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-[#f1f5f9] mt-2 tabular-nums">
            {formatNumber(value)}
          </p>

          {trend !== undefined && (
            <div className="flex items-center gap-1.5 mt-2.5">
              <span
                className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-md ${
                  isPositive
                    ? 'bg-green-500/10 text-[#22c55e]'
                    : isNegative
                    ? 'bg-red-500/10 text-[#ef4444]'
                    : 'bg-[#1c1c2a] text-[#94a3b8]'
                }`}
              >
                {isPositive ? <TrendingUp className="w-3 h-3" /> : isNegative ? <TrendingDown className="w-3 h-3" /> : null}
                {isPositive ? '+' : ''}{trend}%
              </span>
              {trendLabel && (
                <span className="text-xs text-[#64748b]">{trendLabel}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0 ml-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cfg.icon}`}>
            {icon}
          </div>
          {to && (
            <ArrowUpRight className="w-4 h-4 text-[#64748b] opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </div>
  );

  if (!to) return card;

  return (
    <Link
      to={to}
      className="group block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3b82f6]"
      aria-label={`${title}: ${value} - open`}
    >
      {card}
    </Link>
  );
}
